import React, { useState } from "react";
import { useLanguage } from "../i18n/LanguageContext";
import { useSiteSettings } from "../hooks/useSiteSettings";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PROJECT_TYPES = [
  "new-installation",
  "maintenance",
  "overhaul",
  "modification",
  "commissioning",
  "service-partnership",
  "other",
];

const empty = {
  name: "",
  email: "",
  phone: "",
  company: "",
  project_type: "new-installation",
  message: "",
};

export default function Contact() {
  const { t, lang } = useLanguage();
  const { settings } = useSiteSettings();
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);

  // Merge translated defaults with admin-saved overrides
  const ci = settings.contact || {};
  const info = {
    phone: ci.phone || t.contact.info_phone,
    email: ci.email || t.contact.info_email,
    address: ci.address || t.contact.info_address,
    hours: ci[`hours_${lang}`] || t.contact.info_hours,
  };

  const update = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, { ...form, language: lang });
      toast.success(t.contact.success);
      setForm(empty);
    } catch (err) {
      toast.error(t.contact.error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="py-20 md:py-24 bg-white">
      <div className="px-6 md:px-10 lg:px-16">
        <div className="grid grid-cols-12 gap-y-8 mb-12">
          <div className="col-span-12 md:col-span-4">
            <div className="font-mono-tech text-[#1d428a] mb-6">
              <span className="inline-block w-2 h-2 bg-[#df4661] mr-3 align-middle" />
              {t.contact.eyebrow}
            </div>
          </div>
          <div className="col-span-12 md:col-span-8">
            <h2 data-testid="contact-title" className="font-display text-4xl md:text-5xl lg:text-6xl text-[#0A0A0A]">
              {t.contact.title}
            </h2>
            <p className="mt-5 text-[#0A0A0A] text-sm md:text-base max-w-2xl leading-relaxed">
              {t.contact.lead}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-12 gap-10">
          <aside className="col-span-12 md:col-span-4 border-t border-[#1d428a]/20 pt-8">
            <div className="font-mono-tech text-[#df4661]">{t.contact.info_title}</div>
            <ul className="mt-6 space-y-5">
              <li>
                <div className="font-mono-tech text-[#5A6270]">ADDRESS</div>
                <div className="font-display-mid text-xl mt-1 text-[#1d428a]">{info.address}</div>
              </li>
              <li>
                <div className="font-mono-tech text-[#5A6270]">HOURS</div>
                <div className="font-display-mid text-xl mt-1 text-[#1d428a]">{info.hours}</div>
              </li>
              <li>
                <div className="font-mono-tech text-[#5A6270]">PHONE</div>
                <a
                  data-testid="contact-info-phone"
                  href={`tel:${info.phone.replace(/\s+/g, "")}`}
                  className="font-display-mid text-xl mt-1 text-[#0A0A0A] link-underline inline-block"
                >
                  {info.phone}
                </a>
              </li>
              <li>
                <div className="font-mono-tech text-[#5A6270]">EMAIL</div>
                <a
                  data-testid="contact-info-email"
                  href={`mailto:${info.email}`}
                  className="font-display-mid text-xl mt-1 text-[#0A0A0A] link-underline inline-block"
                >
                  {info.email}
                </a>
              </li>
            </ul>

            {/* Map */}
            <div data-testid="contact-map" className="mt-8 border border-[#1d428a]/20">
              <div className="flex items-center justify-between px-3 py-2 border-b border-[#1d428a]/20 bg-[#e8ecf3]">
                <div className="font-mono-tech text-[#1d428a]">
                  <span className="inline-block w-1.5 h-1.5 bg-[#df4661] mr-2 align-middle" />
                  LOCATION · 47.677°N 8.632°E
                </div>
                <a
                  data-testid="contact-map-link"
                  href="https://www.openstreetmap.org/?mlat=47.6772&mlon=8.6328#map=18/47.6772/8.6328"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-mono-tech text-[#df4661] hover:text-[#1d428a] transition-colors"
                >
                  ÖFFNEN →
                </a>
              </div>
              <iframe
                title="Schweizer Planmanufaktur GmbH — Gründenstrasse 82, 8247 Flurlingen"
                src="https://www.openstreetmap.org/export/embed.html?bbox=8.6210%2C47.6730%2C8.6450%2C47.6815&layer=mapnik&marker=47.6772%2C8.6328"
                className="w-full h-64 grayscale-[0.2]"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </aside>

          <form
            data-testid="contact-form"
            onSubmit={submit}
            className="col-span-12 md:col-span-8 border-t border-[#1d428a]/20 pt-8"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-[#1d428a]/15 border border-[#1d428a]/15">
              <Field label={t.contact.fields.name} code="01" required value={form.name} onChange={update("name")} testid="contact-input-name" />
              <Field label={t.contact.fields.email} code="02" required type="email" value={form.email} onChange={update("email")} testid="contact-input-email" />
              <Field label={t.contact.fields.phone} code="03" value={form.phone} onChange={update("phone")} testid="contact-input-phone" />
              <Field label={t.contact.fields.company} code="04" value={form.company} onChange={update("company")} testid="contact-input-company" />
              <div className="bg-white p-5 md:col-span-2">
                <label className="font-mono-tech text-[#df4661] flex items-center gap-3">
                  <span>05</span>
                  <span>{t.contact.fields.project_type} *</span>
                </label>
                <select
                  data-testid="contact-select-project-type"
                  value={form.project_type}
                  onChange={update("project_type")}
                  required
                  className="mt-3 w-full bg-transparent border-0 border-b border-[#1d428a]/30 focus:border-[#df4661] outline-none py-2 font-display-mid text-xl text-[#0A0A0A]"
                >
                  {PROJECT_TYPES.map((pt) => (
                    <option key={pt} value={pt}>
                      {t.projectTypes[pt]}
                    </option>
                  ))}
                </select>
              </div>
              <div className="bg-white p-5 md:col-span-2">
                <label className="font-mono-tech text-[#df4661] flex items-center gap-3">
                  <span>06</span>
                  <span>{t.contact.fields.message} *</span>
                </label>
                <textarea
                  data-testid="contact-input-message"
                  rows={5}
                  required
                  value={form.message}
                  onChange={update("message")}
                  className="mt-3 w-full bg-transparent border-0 border-b border-[#1d428a]/30 focus:border-[#df4661] outline-none py-2 text-[#0A0A0A] resize-none"
                />
              </div>
            </div>

            <button
              type="submit"
              data-testid="contact-submit"
              disabled={loading}
              className="mt-8 inline-flex items-center gap-3 bg-[#1d428a] text-white font-mono-tech px-7 py-4 hover:bg-[#df4661] disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
            >
              <span className="w-1.5 h-1.5 bg-[#df4661]" />
              {loading ? t.contact.submitting : t.contact.submit}
            </button>
          </form>
        </div>
      </div>
    </section>
  );
}

function Field({ label, code, type = "text", value, onChange, required, testid }) {
  return (
    <div className="bg-white p-5">
      <label className="font-mono-tech text-[#df4661] flex items-center gap-3">
        <span>{code}</span>
        <span>
          {label}
          {required && " *"}
        </span>
      </label>
      <input
        data-testid={testid}
        type={type}
        value={value}
        onChange={onChange}
        required={required}
        className="mt-3 w-full bg-transparent border-0 border-b border-[#1d428a]/30 focus:border-[#df4661] outline-none py-2 font-display-mid text-xl text-[#0A0A0A]"
      />
    </div>
  );
}
